import App, { AppContext, AppProps } from 'next/app';
import React, { useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import Cookies from 'universal-cookie';
import { ParallaxProvider } from 'react-scroll-parallax';
import { Provider } from 'react-redux';
import { useRouter } from 'next/router';
import { setupSentry } from '../utils/sentry';
import { UrlGenerator } from '../urls/UrlGenerator';
import { isAuthorized, requireBasicAuth } from '../utils/auth';
import SearchAnswers from '../search/SearchAnswers';
import { NavigationHistoryTracker } from '../components/navigation/NavigationHistoryTracker';
import BasicLayout from '../components/layout/BasicLayout';
import { wrapper } from '../redux/store';
import UserInfoStore from '../user/UserInfoStore';
import {
  activateRemoveOutlines,
  deactivateRemoveOutlines
} from '../utils/removeOutlines';
import WindowContextProvider from '../components/common/WindowContextProvider';
import { initializeAnalytics } from '../analytics/analytics';
import CookieConsent from '../components/common/CookieConsent';
import ErrorPage from './ErrorPage';

setupSentry();

export const BrowserContext = React.createContext({ webpSupported: false });
export const SiteContext = React.createContext({
  host: '',
  urlGenerator: new UrlGenerator('')
});
export const UserContext = React.createContext({
  user: new UserInfoStore(new Cookies())
});
export const SearchContext = React.createContext({
  answers: new SearchAnswers(new Cookies())
});

const normalizeCssUrl =
  'https://cdnjs.cloudflare.com/ajax/libs/normalize/8.0.0/normalize.min.css';

export const MyApp = ({ Component, ...rest }: AppProps) => {
  const { store, props } = wrapper.useWrappedStore(rest);
  const { pageProps, webpSupported, host, isIE, requestCookieHeader } =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props as any) || {};
  const [mounted, setMounted] = useState(false);
  const router = useRouter();

  const cookies = useMemo(
    () => new Cookies(requestCookieHeader),
    [requestCookieHeader]
  );

  useEffect(() => {
    activateRemoveOutlines();
    setMounted(true);
    initializeAnalytics();
    return () => {
      deactivateRemoveOutlines();
    };
  }, []);

  const renderNormalizeCSS = () => (
    <>
      <Head>
        <link
          rel={mounted ? 'stylesheet' : 'preload'}
          href={normalizeCssUrl}
          as="style"
        />
      </Head>
      <noscript>
        <link rel="stylesheet" href={normalizeCssUrl} />
      </noscript>
    </>
  );

  const getDefaultLayout = (page: React.ReactNode) => <>{page}</>;

  if (pageProps?.httpErrorCode || isIE) {
    return (
      <Provider store={store}>
        <BasicLayout>
          <ErrorPage
            statusCode={pageProps?.httpErrorCode}
            isIE={isIE}
            {...pageProps?.errorPageProps}
          />
        </BasicLayout>
      </Provider>
    );
  }

  const getLayout =
    //@ts-ignore -- Adding "getLayout" to the NextComponentType is so complex that we just ignore the error
    Component.getLayout || getDefaultLayout;

  return (
    <Provider store={store}>
      <ParallaxProvider>
        <BrowserContext.Provider value={{ webpSupported }}>
          <SiteContext.Provider
            value={{ host, urlGenerator: new UrlGenerator(host) }}
          >
            <UserContext.Provider
              value={{
                user: new UserInfoStore(cookies)
              }}
            >
              <SearchContext.Provider
                value={{
                  answers: new SearchAnswers(cookies)
                }}
              >
                <NavigationHistoryTracker asPath={router.asPath}>
                  <WindowContextProvider>
                    {renderNormalizeCSS()}
                    <BasicLayout>
                      {getLayout(<Component {...pageProps} />)}
                    </BasicLayout>
                    <CookieConsent />
                  </WindowContextProvider>
                </NavigationHistoryTracker>
              </SearchContext.Provider>
            </UserContext.Provider>
          </SiteContext.Provider>
        </BrowserContext.Provider>
      </ParallaxProvider>
    </Provider>
  );
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  const { req, res } = appContext.ctx;
  let webpSupported = false;
  let host = '';
  let requestCookieHeader;

  if (req && res) {
    if (!isAuthorized(req)) {
      requireBasicAuth(res);
    }
    webpSupported = MyApp.webpIsSupported(String(req.headers.accept));
    host = String(req.headers.host);
    requestCookieHeader = req.headers.cookie;
  } else {
    host = typeof window !== 'undefined' ? window.location.host : '';
  }
  const appProps = await App.getInitialProps(appContext);

  let isIE = false;
  if (req && req.headers && req.headers['user-agent']) {
    isIE = /Trident/.test(req.headers['user-agent']);
  }

  // A page can return httpErrorCode from getInitialProps in order to render an error page like 404
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageProps = appProps.pageProps as any;
  if (res && pageProps.httpErrorCode && !isIE) {
    if (ErrorPage.getInitialProps) {
      // We are in an user-defined error, let's fetch the Error component
      // getInitialProps if needed, and merge them to pageProps with the error object
      const error: Error & {
        statusCode?: number;
      } = new Error('Unexpected http status code received from page props');
      error.statusCode = pageProps.httpErrorCode;
      pageProps.errorPageProps = await ErrorPage.getInitialProps({
        ...appContext.ctx,
        err: error
      });
    }
    res.statusCode = pageProps.httpErrorCode;
  }

  // Call error page initialProps manually when IE is detected so that the Error-page will get proper props
  if (isIE && ErrorPage.getInitialProps) {
    pageProps.errorPageProps = await ErrorPage.getInitialProps({
      ...appContext.ctx
    });
  }

  return { webpSupported, host, requestCookieHeader, isIE, ...appProps };
};

MyApp.webpIsSupported = (acceptHeader: string) => {
  return /image\/webp/.test(acceptHeader);
};

export default MyApp;
